import React, { useState, useMemo } from 'react';
import { Table } from 'react-bootstrap';

export interface ColumnDef {
  value: string;
  label: string;
}

export interface ColumnRenderItem {
  column: string;
  render: (
    value: unknown,
    col: ColumnDef,
    colIndex: number,
    rowData: Record<string, unknown>,
    rowIndex: number,
  ) => React.ReactNode;
}

export interface AddProps {
  tHead?: React.HTMLAttributes<HTMLTableSectionElement>;
  tHeadRow?: React.HTMLAttributes<HTMLTableRowElement>;
  tBodyRow?: (
    rowData: Record<string, unknown>,
    rowIndex: number,
  ) => React.HTMLAttributes<HTMLTableRowElement>;
  tHeading?: (
    col: ColumnDef,
    colIndex: number,
  ) => React.HTMLAttributes<HTMLTableCellElement>;
  tData?: (
    value: unknown,
    col: ColumnDef,
    colIndex: number,
    rowData: Record<string, unknown>,
    rowIndex: number,
  ) => React.HTMLAttributes<HTMLTableCellElement>;
  tBody?: React.HTMLAttributes<HTMLTableSectionElement>;
  firstColumn?: (
    rowData: Record<string, unknown>,
    rowIndex: number,
  ) => React.HTMLAttributes<HTMLTableCellElement>;
  lastColumn?: (
    rowData: Record<string, unknown>,
    rowIndex: number,
  ) => React.HTMLAttributes<HTMLTableCellElement>;
}

export interface SortableTableProps {
  data?: Record<string, unknown>[];
  setData?: React.Dispatch<React.SetStateAction<Record<string, unknown>[]>>;
  columns?: (ColumnDef | string)[];
  dateColumns?: string[];
  noSortColumns?: string[];
  columnRender?: ColumnRenderItem[];
  addProps?: AddProps;
  isSortable?: boolean;
  striped?: boolean;
  hover?: boolean;
  bordered?: boolean;
  [key: string]: unknown;
}

function sortComparator(isAsc: boolean, columnName: string, isDate: boolean) {
  return (a: Record<string, unknown>, b: Record<string, unknown>) => {
    if (isDate) {
      try {
        const aTime = new Date(a[columnName] as string).getTime();
        const bTime = new Date(b[columnName] as string).getTime();
        return isAsc ? aTime - bTime : bTime - aTime;
      } catch {
        return 0;
      }
    }

    const aVal = a[columnName] ?? '';
    const bVal = b[columnName] ?? '';
    const aStr = String(aVal);
    const bStr = String(bVal);

    const isNumericStr = (v: string): boolean =>
      String(+v) === v && v.length > 0;
    const aParts = aStr.match(/\d+|\D+/g) ?? [];
    const bParts = bStr.match(/\d+|\D+/g) ?? [];

    let i = 0;
    const len = Math.min(aParts.length, bParts.length);

    while (i < len && aParts[i] === bParts[i]) {
      i += 1;
    }

    if (i === len) {
      return isAsc
        ? aParts.length - bParts.length
        : bParts.length - aParts.length;
    }

    if (isNumericStr(aParts[i]) && isNumericStr(bParts[i])) {
      const diff = +aParts[i] - +bParts[i];
      return isAsc ? diff : -diff;
    }

    const cmp = aParts[i].localeCompare(bParts[i]);
    return isAsc ? cmp : -cmp;
  };
}

function toTitleCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function renderCell(
  value: unknown,
  col: ColumnDef,
  colIndex: number,
  rowData: Record<string, unknown>,
  rowIndex: number,
  columnRender: ColumnRenderItem[],
): React.ReactNode {
  const match = columnRender.find(cr => cr.column === col.value);
  if (match) {
    return match.render(value, col, colIndex, rowData, rowIndex);
  }
  return value as React.ReactNode;
}

export const SortableTable: React.FC<SortableTableProps> = ({
  data = [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setData,
  columns,
  dateColumns = [],
  noSortColumns = [],
  columnRender = [],
  addProps = {},
  isSortable = true,
  striped = true,
  hover = true,
  bordered = false,
  ...tableProps
}) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState<boolean>(true);

  // Normalize columns
  const effectiveColumns = useMemo<ColumnDef[]>(() => {
    if (columns && columns.length > 0) {
      return columns.map(col =>
        typeof col === 'string' ? { value: col, label: toTitleCase(col) } : col,
      );
    }
    if (data && data.length > 0) {
      return Object.keys(data[0]).map(k => ({
        value: k,
        label: toTitleCase(k),
      }));
    }
    return [];
  }, [columns, data]);

  // Compute sorted data
  const sortedData = useMemo(() => {
    if (!sortColumn || !data || data.length === 0) {
      return data || [];
    }
    const isDate = dateColumns.includes(sortColumn);
    return [...data].sort(sortComparator(sortAsc, sortColumn, isDate));
  }, [data, sortColumn, sortAsc, dateColumns]);

  // Handle header click
  const handleHeaderClick = (colValue: string) => {
    if (!isSortable || !data || data.length === 0) return;
    if (noSortColumns.includes(colValue)) return;

    if (sortColumn === colValue) {
      setSortAsc(prev => !prev);
    } else {
      setSortColumn(colValue);
      setSortAsc(true);
    }
  };

  // Get sort icon for column
  const getSortIcon = (colValue: string): string | null => {
    if (!isSortable || noSortColumns.includes(colValue)) {
      return null;
    }
    if (sortColumn === colValue) {
      return sortAsc ? ' ↑' : ' ↓';
    }
    return null;
  };

  return (
    <Table
      striped={striped}
      hover={hover}
      bordered={bordered}
      responsive
      {...tableProps}
    >
      <thead {...addProps.tHead}>
        <tr {...addProps.tHeadRow}>
          {effectiveColumns.map((col, colIndex) => {
            const headingAttrs = addProps.tHeading
              ? addProps.tHeading(col, colIndex)
              : {};
            const isHeaderSortable =
              isSortable && !noSortColumns.includes(col.value);
            return (
              <th
                key={col.value}
                onClick={() => handleHeaderClick(col.value)}
                style={{
                  cursor: isHeaderSortable ? 'pointer' : 'default',
                  ...headingAttrs?.style,
                }}
                {...headingAttrs}
              >
                {col.label}
                {getSortIcon(col.value)}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody {...addProps.tBody}>
        {sortedData.map((rowData, rowIndex) => {
          const rowAttrs = addProps.tBodyRow
            ? addProps.tBodyRow(rowData, rowIndex)
            : {};
          const rowKey = (rowData.id as string) || `row-${rowIndex}`;
          return (
            <tr key={rowKey} {...rowAttrs}>
              {effectiveColumns.map((col, colIndex) => {
                const cellValue = rowData[col.value];
                const cellAttrs = addProps.tData
                  ? addProps.tData(cellValue, col, colIndex, rowData, rowIndex)
                  : {};
                let finalAttrs = { ...cellAttrs };

                // Apply firstColumn/lastColumn if provided
                if (colIndex === 0 && addProps.firstColumn) {
                  const fcAttrs = addProps.firstColumn(rowData, rowIndex);
                  finalAttrs = { ...finalAttrs, ...fcAttrs };
                }
                if (
                  colIndex === effectiveColumns.length - 1 &&
                  addProps.lastColumn
                ) {
                  const lcAttrs = addProps.lastColumn(rowData, rowIndex);
                  finalAttrs = { ...finalAttrs, ...lcAttrs };
                }

                return (
                  <td key={`${rowKey}-${col.value}`} {...finalAttrs}>
                    {renderCell(
                      cellValue,
                      col,
                      colIndex,
                      rowData,
                      rowIndex,
                      columnRender,
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};
